import React from "react";
import { useTheme } from "../context/ThemeContext";
import "./NotesPage.css";

const NotesPage = () => {
  const { theme } = useTheme();

  return (
    <div
      className="notes-page"
      data-theme={theme}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        minHeight: "70vh",
      }}
    >
      <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>📚</div>
      <h1 style={{ fontSize: "2rem", fontWeight: "700", marginBottom: "0.75rem" }}>
        Notes Not Available
      </h1>
      <p style={{ fontSize: "1.1rem", color: "var(--text-secondary-light)", maxWidth: "400px" }}>
        Sorry, study notes are not available right now. Please check back later or contact your teacher for assistance.
      </p>

    </div>
  );
};

export default NotesPage;

/*
===== ORIGINAL NOTES PAGE CODE (commented out) =====

import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "../context/ThemeContext";
import { db } from "../firebase/config";
import { collection, getDocs, query, orderBy, doc, setDoc, increment, deleteDoc } from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import {
  Clock,
  Info,
  Zap,
  ShieldAlert,
  Megaphone
} from "lucide-react";
import "./NotesPage.css";

const NotesPage = () => {
  const { theme } = useTheme();
  const [notes, setNotes] = useState([]);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessKey, setAccessKey] = useState("");
  const [error, setError] = useState("");

  const validKeys = ["Sanjay-Thakur", "Cyclotron", "cyclotron", "Harman"];

  useEffect(() => {
    const isAuth = sessionStorage.getItem("notesAuthenticated");
    if (isAuth === "true") {
      setIsAuthenticated(true);
    }
    fetchNotes();
    fetchNotices();
    updateActiveStatus();

    // Active User Heartbeat (every 30 seconds)
    const heartbeat = setInterval(() => {
      updateActiveStatus();
    }, 30000);

    return () => clearInterval(heartbeat);
  }, []);

  const fetchNotices = async () => {
    try {
      const q = query(collection(db, "notices"), orderBy("postedAt", "desc"));
      const snapshot = await getDocs(q);
      const allNotices = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const now = Date.now();
      const activeNotices = [];
      const expiredIds = [];

      allNotices.forEach(notice => {
        if (notice.expiresAt && notice.expiresAt < now) {
          expiredIds.push(notice.id);
        } else {
          activeNotices.push(notice);
        }
      });

      // Lazy cleanup of expired notices
      expiredIds.forEach(async (id) => {
        try {
          await deleteDoc(doc(db, "notices", id));
        } catch (e) {
          console.error("Cleanup error:", e);
        }
      });

      // Sort by priority (5 to 1) then by date
      activeNotices.sort((a, b) => (b.priority || 0) - (a.priority || 0));
      setNotices(activeNotices.slice(0, 5)); // Limit to 5
    } catch (err) {
      console.error("Error fetching notices:", err);
    }
  };

  const updateActiveStatus = async () => {
    try {
      const sessionId = sessionStorage.getItem('ebi_session_id') || Math.random().toString(36).substring(7);
      sessionStorage.setItem('ebi_session_id', sessionId);

      const sessionRef = doc(db, 'active_sessions', sessionId);
      await setDoc(sessionRef, {
        lastSeen: new Date().getTime(),
        path: window.location.pathname
      }, { merge: true });
    } catch (err) {
      // Quiet fail
    }
  };

  const fetchNotes = async () => {
    try {
      const q = query(collection(db, "notes"), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const notesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setNotes(notesData);
    } catch (error) {
      console.error("Error fetching notes:", error);
    } finally {
      setLoading(false);
    }
  };

  const getClassEmoji = (name) => {
    const emojis = {
      "Class 8": "8️⃣",
      "Class 9": "9️⃣",
      "Class 10": "🔟",
      "Class 11": "🎒",
      "Class 12": "🎓",
      "JEE": "🚀",
      "NEET": "🧬"
    };
    return emojis[name] || "📚";
  };

  const getSubjectEmoji = (name) => {
    const emojis = {
      "Physics": "⚛️",
      "Chemistry": "🧪",
      "Mathematics": "📐",
      "Biology": "🧬",
      "English": "📖"
    };
    return emojis[name] || "📝";
  };

  const classesData = useMemo(() => {
    if (!notes.length) return [];

    const classesMap = {};

    // Sort notes by originalIndex first to maintain global relative order
    const sortedNotes = [...notes].sort((a, b) => (a.originalIndex || 0) - (b.originalIndex || 0));

    sortedNotes.forEach(note => {
      const className = note.className || "Other";
      const subjectName = note.subject || "General";
      const chapterName = note.chapter || "Miscellaneous";

      if (!classesMap[className]) {
        classesMap[className] = {
          id: className,
          name: className,
          emoji: getClassEmoji(className),
          subjects: {},
          firstIndex: note.originalIndex || 0 // Store first index to sort classes
        };
      }

      if (!classesMap[className].subjects[subjectName]) {
        classesMap[className].subjects[subjectName] = {
          id: `${className}-${subjectName}`,
          name: subjectName,
          emoji: getSubjectEmoji(subjectName),
          chapters: {},
          firstIndex: note.originalIndex || 0
        };
      }

      if (!classesMap[className].subjects[subjectName].chapters[chapterName]) {
        classesMap[className].subjects[subjectName].chapters[chapterName] = {
          id: `${className}-${subjectName}-${chapterName}`,
          name: chapterName,
          parts: [],
          firstIndex: note.originalIndex || 0
        };
      }

      classesMap[className].subjects[subjectName].chapters[chapterName].parts.push({
        name: note.part || 'Complete Notes',
        url: note.driveLink
      });
    });

    // Convert objects back to arrays and sort by their first appearance
    return Object.values(classesMap)
      .sort((a, b) => a.firstIndex - b.firstIndex)
      .map(cls => ({
        ...cls,
        subjects: Object.values(cls.subjects)
          .sort((a, b) => a.firstIndex - b.firstIndex)
          .map(sub => ({
            ...sub,
            chapters: Object.values(sub.chapters).sort((a, b) => a.firstIndex - b.firstIndex)
          }))
      }));
  }, [notes]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (validKeys.includes(accessKey.trim())) {
      setIsAuthenticated(true);
      setError("");
      sessionStorage.setItem("notesAuthenticated", "true");
    } else {
      setError("Invalid access key. Please try again.");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAccessKey("");
    sessionStorage.removeItem("notesAuthenticated");
  };

  const handleBackToClasses = () => {
    setSelectedClass(null);
    setSelectedSubject(null);
  };

  const handleBackToSubjects = () => {
    setSelectedSubject(null);
  };

  const handleSubjectClick = (subject) => {
    setSelectedSubject(subject);
  };

  const trackDownload = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];

      // 1. Global Stats
      const statsRef = doc(db, 'analytics', 'site_stats');
      await setDoc(statsRef, {
        downloads: increment(1)
      }, { merge: true });

      // 2. Daily Stats
      const dailyRef = doc(db, 'analytics', 'site_stats', 'daily_stats', today);
      await setDoc(dailyRef, {
        downloads: increment(1),
        date: today
      }, { merge: true });
    } catch (error) {
      console.error("Error tracking download:", error);
    }
  };

  const handlePartClick = (url) => {
    trackDownload();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!isAuthenticated) {
    return (
      <div className="notes-page" data-theme={theme}>
        <section className="notes-header">
          <h1 className="gradient-text">Study Notes</h1>
          <p className="subtitle">Enter access key to view notes</p>
        </section>

        <div className="auth-container">
          <div className="auth-card">
            <h2>Access Required</h2>
            <p>Please enter the access key to view the study notes:</p>

            <form onSubmit={handleLogin} className="auth-form">
              <div className="input-group">
                <input
                  type="password"
                  value={accessKey}
                  onChange={(e) => setAccessKey(e.target.value)}
                  placeholder="Enter access key"
                  className="auth-input"
                  required
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <button type="submit" className="auth-button">
                Access Notes
              </button>
            </form>

            <div className="auth-instructions">
              <h4>How to get access:</h4>
              <p>Contact your teacher or administrator for the access key.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="notes-page" data-theme={theme}>
        <div className="loading-container">
          <div className="loader"></div>
          <p>Fetching dynamic notes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notes-page" data-theme={theme}>
      <section className="notes-header">
        <AnimatePresence>
          {notices.length > 0 && !selectedClass && !selectedSubject && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="notice-board-container"
            >
              <div className="notice-board-header">
                <Megaphone size={20} className="text-blue-500" />
                <h2>LIVE NOTICE BOARD</h2>
                <div className="live-dot"></div>
              </div>

              <div className="notices-grid">
                {notices.map((notice) => {
                  const colors = {
                    '1': 'blue',
                    '2': 'cyan',
                    '3': 'amber',
                    '4': 'orange',
                    '5': 'red'
                  };
                  const color = colors[notice.priority] || 'blue';

                  return (
                    <motion.div
                      key={notice.id}
                      whileHover={{ scale: 1.02 }}
                      className={`notice-item priority-${color}`}
                    >
                      <div className="notice-icon">
                        {notice.priority >= 4 ? <ShieldAlert size={18} /> :
                          notice.priority >= 3 ? <Zap size={18} /> :
                            <Info size={18} />}
                      </div>
                      <div className="notice-content">
                        <h3>{notice.title}</h3>
                        <p>{notice.content}</p>
                        <div className="notice-meta">
                          <Clock size={10} />
                          <span>Expires in {Math.max(1, Math.round((notice.expiresAt - Date.now()) / (1000 * 60 * 60)))}h</span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="header-top">
          <h1 className="gradient-text">Study Notes</h1>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
        <p className="subtitle">
          {selectedSubject
            ? `Chapters for ${selectedSubject.name} - ${selectedClass.name}`
            : selectedClass
              ? `Select a subject from ${selectedClass.name}`
              : "Select a class to view available notes"}
        </p>
      </section>

      {notes.length === 0 ? (
        <div className="empty-state">
          <p>No notes found in the database. Please contact the administrator.</p>
        </div>
      ) : !selectedClass ? (
        <div className="classes-grid">
          {classesData.map((classItem) => (
            <div
              key={classItem.id}
              className="class-card"
              onClick={() => setSelectedClass(classItem)}
            >
              <div className="class-emoji">{classItem.emoji}</div>
              <h3>{classItem.name}</h3>
              <div className="subjects-count">
                {classItem.subjects.length} subject
                {classItem.subjects.length !== 1 ? "s" : ""}
              </div>
              <div className="hover-overlay"></div>
            </div>
          ))}
        </div>
      ) : !selectedSubject ? (
        <div className="subjects-view">
          <button className="back-button" onClick={handleBackToClasses}>
            ← Back to Classes
          </button>

          <h2 className="class-title">{selectedClass.name} Subjects</h2>

          <div className="subjects-grid">
            {selectedClass.subjects.map((subject) => (
              <div
                key={subject.id}
                className="subject-card"
                onClick={() => handleSubjectClick(subject)}
              >
                <div className="subject-emoji">{subject.emoji}</div>
                <h3>{subject.name}</h3>
                <div className="chapters-count">
                  {subject.chapters.length} chapter
                  {subject.chapters.length !== 1 ? "s" : ""}
                </div>
                <div className="hover-overlay"></div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="chapters-view">
          <div className="navigation-buttons">
            <button className="back-button" onClick={handleBackToSubjects}>
              ← Back to Subjects
            </button>
            <button
              className="back-button back-to-classes"
              onClick={handleBackToClasses}
            >
              ← Back to Classes
            </button>
          </div>

          <h2 className="class-title">
            <span className="subject-emoji">{selectedSubject.emoji}</span>
            {selectedSubject.name} - {selectedClass.name}
          </h2>

          <div className="chapters-list">
            {selectedSubject.chapters.map((chapter) => (
              <div key={chapter.id} className="chapter-card">
                <h3 className="chapter-name">{chapter.name}</h3>

                <div className="chapter-parts">
                  {chapter.parts.map((part, index) => (
                    <div
                      key={index}
                      className="part-item"
                      onClick={() => handlePartClick(part.url)}
                    >
                      <span className="part-name">{part.name}</span>
                      <span className="download-icon">↓</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="github-instructions">
        <h3>How to download notes:</h3>
        <ol>
          <li>Click on any chapter part</li>
          <li>The notes will open in a new tab</li>
          <li>Use the download option on that page</li>
        </ol>
      </div>
    </div>
  );
};

export default NotesPage;
*/
